import React, { useState, useEffect, useRef } from 'react';
import { Edit2, Trash2, Plus, X, Search, Boxes, PackageX, AlertTriangle, Package } from 'lucide-react';
import { productService } from '../../services/productService';
import { vendorService } from '../../services/vendorService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const Products = () => {
  const { user, isAdmin, isVendor } = useAuth();
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [myVendor, setMyVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [vendorFilter, setVendorFilter] = useState('All');
  const [lowStockFilter, setLowStockFilter] = useState(false);
  
  
  // Refs for file inputs
  const localFileRef = useRef(null);
  const cameraFileRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    mrp: '',
    distributorPrice: '',
    category: 'Electronics',
    images: [], // Upgraded from single image string to structural array
    stock: '',
    vendor: '',
    featured: false,
    condition: 'New'
  });

  useEffect(() => {
    fetchProducts();
    fetchVendors();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await productService.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Upload image to Cloudinary via backend and push into images array array
  const handleImageChange = async (e, isCamera = false) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append("image", file);

    const token = localStorage.getItem("token");

    try {
      const apiBase = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const res = await fetch(
        `${apiBase}/products/upload-image`,

        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: data,
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Upload failed");
      }

      const json = await res.json();

      // Append new image node to array state tracker array
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, { url: json.imageUrl, publicId: json.publicId }]
      }));

      toast.success("Image uploaded successfully!");
      
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.message || "Image upload failed");
    } finally {
      if (localFileRef.current) localFileRef.current.value = '';
      if (cameraFileRef.current) cameraFileRef.current.value = '';
    }
  };

  const removeImageFromArray = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

const fetchProducts = async () => {
    try {
      // Pass inventory=true to backend so that vendor-scoped access is enforced
      // (vendors can only see their own products in the admin panel).
      // The public Store page does NOT send this flag, so vendors browsing as
      // shoppers will see all products.
      const params = { inventory: true };
      if (isAdmin && vendorFilter !== 'All') {
        params.vendor = vendorFilter;
      }
      const response = await productService.getAllProducts(params);
      setProducts(response.data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await vendorService.getAllVendors();
      const allVendors = response.data;
      
      // If user is vendor, find their own vendor profile
      if (isVendor && user) {
        const myVendorProfile = allVendors.find(v => 
          v.user && v.user._id === user._id
        );
        setMyVendor(myVendorProfile || null);
        // Show only the vendor's own profile in dropdown
        setVendors(myVendorProfile ? [myVendorProfile] : []);
      } else {
        // Admin sees all vendors
        setVendors(allVendors);
      }
    } catch (error) {
      toast.error('Failed to load vendors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await productService.updateProduct(editingProduct._id, formData);
        toast.success('Product updated successfully!');
      } else {
        await productService.createProduct(formData);
        toast.success('Product created successfully!');
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    
    // Safety check map to translate legacy singular columns cleanly if they come up
    let productImagesArray = [];
    if (product.images && product.images.length > 0) {
      productImagesArray = product.images.map(img => ({
        url: img.url || img,
        publicId: img.publicId || ''
      }));
    } else if (product.image) {
      productImagesArray = [{ url: product.image, publicId: product.imagePublicId || '' }];
    }

    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      mrp: product.mrp || '',
      distributorPrice: product.distributorPrice || '',
      category: product.category,
      images: productImagesArray,
      stock: product.stock,
      vendor: product.vendor?._id || product.vendor,
      featured: product.featured || false,
      condition: product.condition || 'New'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.deleteProduct(id);
        toast.success('Product deleted successfully!');
        fetchProducts();
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      mrp: '',
      distributorPrice: '',
      category: 'Electronics',
      images: [],
      stock: '',
      vendor: '',
      featured: false,
      condition: 'New'
    });
    setEditingProduct(null);
    if (localFileRef.current) localFileRef.current.value = '';
    if (cameraFileRef.current) cameraFileRef.current.value = '';
  };

  // Combined filtering: search + category + vendor (client side for responsive UI)
  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'All' || p.category === categoryFilter;
    const matchesVendor =
      vendorFilter === 'All' ||
      (p.vendor &&
        (p.vendor._id === vendorFilter || p.vendor === vendorFilter));
    const matchesLowStock = !lowStockFilter || p.stock <= 10;
    return matchesSearch && matchesCategory && matchesVendor && matchesLowStock;
  });

  // Inventory summary stats
  const totalStockUnits = products.reduce((sum, p) => sum + (p.stock || 0), 0);
  const lowStockCount = products.filter((p) => p.stock > 0 && p.stock <= 10).length;
  const outOfStockCount = products.filter((p) => !p.stock || p.stock === 0).length;

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Products Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all"
        >
          <Plus size={20} />
          Add Product
        </button>
      </div>

      {/* Inventory Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-5 flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600">
            <Package className="text-white" size={22} />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Total Products</p>
            <p className="text-2xl font-bold text-gray-800">{products.length}</p>
          </div>
        </div>
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-5 flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500">
            <Boxes className="text-white" size={22} />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Total Stock Units</p>
            <p className="text-2xl font-bold text-gray-800">{totalStockUnits}</p>
          </div>
        </div>
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-5 flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500">
            <AlertTriangle className="text-white" size={22} />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Low Stock (&le;10)</p>
            <p className="text-2xl font-bold text-amber-600">{lowStockCount}</p>
          </div>
        </div>
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-5 flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-500">
            <PackageX className="text-white" size={22} />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Out of Stock</p>
            <p className="text-2xl font-bold text-red-600">{outOfStockCount}</p>
          </div>
        </div>
      </div>

      {/* Filter Controls: Search + Category + Vendor + Low Stock */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm"
            />
          </div>

          {/* Category Filter (Admin + Vendor) */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm bg-white"
            >
              <option value="All">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Vendor Filter (Admin only - vendors always scoped to own products) */}
          {isAdmin && (
            <div>
              <select
                value={vendorFilter}
                onChange={(e) => {
                  setVendorFilter(e.target.value);
                  // Re-fetch products when vendor filter changes so the backend
                  // returns only that vendor's inventory for the admin.
                  fetchProducts();
                }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm bg-white"
              >
                <option value="All">All Vendors</option>
                {vendors.map((vendor) => (
                  <option key={vendor._id} value={vendor._id}>{vendor.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Low Stock Toggle */}
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={lowStockFilter}
                onChange={(e) => setLowStockFilter(e.target.checked)}
                className="w-4 h-4 text-amber-500 rounded focus:ring-2 focus:ring-amber-500"
              />
              Low Stock Only (&le;10)
            </label>
          </div>
        </div>
      </div>

      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left">Image</th>
                <th className="px-6 py-4 text-left">Name</th>
                <th className="px-6 py-4 text-left">Category</th>
                <th className="px-6 py-4 text-left">Price</th>
                <th className="px-6 py-4 text-left">Stock</th>
                <th className="px-6 py-4 text-left">Vendor</th>
                <th className="px-6 py-4 text-left">Rating</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => {
                const displayImage = product.images?.[0]?.url || product.image || '';
                return (
                  <tr key={product._id} className="border-b hover:bg-purple-50 transition-colors">
                    <td className="px-6 py-4">
                      <img
                        src={displayImage}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-lg border bg-slate-50"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-800">{product.name}</div>
                      <div className="text-sm text-gray-500 line-clamp-1">{product.description}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {product.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-bold text-purple-600">₹{product.price}</td>
                    <td className="px-6 py-4">
                      {product.stock <= 0 ? (
                        <span className="px-3 py-1 rounded-full text-sm bg-red-100 text-red-700 font-medium">
                          Out of Stock
                        </span>
                      ) : product.stock <= 10 ? (
                        <span className="px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-700 font-medium">
                          {product.stock} units (Low)
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">
                          {product.stock} units
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{product.vendor?.name || 'N/A'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <span className="text-yellow-500">⭐</span>
                        <span className="font-medium">{product.rating}</span>
                        <span className="text-gray-500 text-sm">({product.numReviews})</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(product._id)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <PackageX className="mx-auto text-gray-300 mb-3" size={48} />
                    <p className="text-gray-500 text-lg font-medium">No products found</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Try adjusting your search or filter criteria
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto shadow-2xl">
            <button
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Name */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                {/* Description */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                {/* Condition */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                  <select
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  >
                    <option value="New">New</option>
                    <option value="Refurbished">Refurbished</option>
                  </select>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                {/* Stock */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stock</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                {/* MRP */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">MRP</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.mrp}
                    onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>

                {/* Distributor Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Distributor Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.distributorPrice}
                    onChange={(e) => setFormData({ ...formData, distributorPrice: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>

                {/* Category with ALL original options untouched */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  >
                    <option value="" disabled>Select Category</option>
                    {/* Electronics */}
                    <option value="Electronics">Electronics</option>
                    <option value="Mobiles & Accessories">Mobiles & Accessories</option>
                    <option value="Computers & Accessories">Computers & Accessories</option>
                    <option value="Audio & Video">Audio & Video</option>
                    <option value="Cameras & Accessories">Cameras & Accessories</option>
                    <option value="Smart Devices">Smart Devices</option>
                    <option value="Smartphones">Smartphones</option>
                    <option value="Laptops">Laptops</option>
                    <option value="Tablets">Tablets</option>
                    <option value="Headphones">Headphones</option>
                    <option value="Televisions">Televisions</option>
                    <option value="Home Appliances">Home Appliances</option>
                    <option value="Gaming Consoles">Gaming Consoles</option>
                    {/* Fashion */}
                    <option value="Fashion">Fashion</option>
                    <option value="Men Clothing">Men Clothing</option>
                    <option value="Women Clothing">Women Clothing</option>
                    <option value="Kids Clothing">Kids Clothing</option>
                    <option value="Footwear">Footwear</option>
                    <option value="Fashion Accessories">Fashion Accessories</option>
                    <option value="Watches">Watches</option>
                    <option value="Jewelry">Jewelry</option>
                    <option value="Bags">Bags</option>
                    {/* Home & Living */}
                    <option value="Home & Living">Home & Living</option>
                    <option value="Furniture">Furniture</option>
                    <option value="Home Decor">Home Decor</option>
                    <option value="Kitchen & Dining">Kitchen & Dining</option>
                    <option value="Kitchenware">Kitchenware</option>
                    <option value="Bedding & Bath">Bedding & Bath</option>
                    <option value="Storage Containers">Storage Containers</option>
                    {/* Beauty & Personal Care */}
                    <option value="Beauty & Personal Care">Beauty & Personal Care</option>
                    <option value="Makeup">Makeup</option>
                    <option value="Skincare">Skincare</option>
                    <option value="Haircare">Haircare</option>
                    <option value="Personal Care Appliances">Personal Care Appliances</option>
                    {/* Grocery & Essentials */}
                    <option value="Grocery & Essentials">Grocery & Essentials</option>
                    <option value="Food & Beverages">Food & Beverages</option>
                    <option value="Fruits & Vegetables">Fruits & Vegetables</option>
                    <option value="Dairy & Bakery">Dairy & Bakery</option>
                    <option value="Household Supplies">Household Supplies</option>
                    {/* Health & Wellness */}
                    <option value="Health & Wellness">Health & Wellness</option>
                    <option value="Medical Supplies">Medical Supplies</option>
                    <option value="Fitness Equipment">Fitness Equipment</option>
                    <option value="Supplements">Supplements</option>
                    {/* Sports & Outdoor */}
                    <option value="Sports & Outdoor">Sports & Outdoor</option>
                    <option value="Sports Equipment">Sports Equipment</option>
                    <option value="Fitness Gear">Fitness Gear</option>
                    <option value="Gym Equipment">Gym Equipment</option>
                    <option value="Yoga Mats">Yoga Mats</option>
                    <option value="Outdoor & Adventure">Outdoor & Adventure</option>
                    {/* Books & Education */}
                    <option value="Books & Education">Books & Education</option>
                    <option value="Books">Books</option>
                    <option value="Stationery">Stationery</option>
                    <option value="Educational Tools">Educational Tools</option>
                    {/* Toys & Baby */}
                    <option value="Toys & Baby">Toys & Baby</option>
                    <option value="Toys">Toys</option>
                    <option value="Baby Products">Baby Products</option>
                    <option value="Baby Care">Baby Care</option>
                    <option value="Kids Accessories">Kids Accessories</option>
                    <option value="Action Figures">Action Figures</option>
                    <option value="Remote Control Cars">Remote Control Cars</option>
                    {/* Automotive */}
                    <option value="Automotive">Automotive</option>
                    <option value="Vehicle Accessories">Vehicle Accessories</option>
                    <option value="Spare Parts">Spare Parts</option>
                    <option value="Tools & Equipment">Tools & Equipment</option>
                    <option value="Helmets">Helmets</option>
                    {/* Office & Industrial */}
                    <option value="Office & Industrial">Office & Industrial</option>
                    <option value="Office Supplies">Office Supplies</option>
                    <option value="Industrial Tools">Industrial Tools</option>
                    <option value="Printers">Printers</option>
                    {/* Digital Products */}
                    <option value="Digital Products">Digital Products</option>
                    <option value="Software">Software</option>
                    <option value="Subscriptions">Subscriptions</option>
                    <option value="Gift Cards">Gift Cards</option>
                    {/* Specialty Categories */}
                    <option value="Pet Supplies">Pet Supplies</option>
                    <option value="Handmade Products">Handmade Products</option>
                    <option value="Eco-Friendly Products">Eco-Friendly Products</option>
                    <option value="Customized Products">Customized Products</option>
                    <option value="Collectibles">Collectibles</option>
                  </select>
                </div>

{/* Vendor Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vendor</label>
                  {isVendor ? (
                    // Vendors are locked to their own store - backend enforces this too
                    <select
                      value={formData.vendor}
                      onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-gray-50"
                      disabled
                    >
                      {vendors.map((vendor) => (
                        <option key={vendor._id} value={vendor._id}>{vendor.name} (My Store)</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={formData.vendor}
                      onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    >
                      <option value="">Select Vendor</option>
                      {vendors.map((vendor) => (
                        <option key={vendor._id} value={vendor._id}>{vendor.name}</option>
                      ))}
                    </select>
                  )}
                  {isVendor && myVendor && (
                    <p className="text-xs text-gray-500 mt-1">Products are assigned to your store automatically</p>
                  )}
                </div>

                {/* Multiple Images Array Section Component View */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Gallery Array ({formData.images.length} uploaded)
                  </label>
                  
                  {/* Grid layout representation maps uploaded assets */}
                  {formData.images.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-4 border p-3 rounded-xl bg-slate-50/50">
                      {formData.images.map((img, idx) => (
                        <div key={idx} className="relative inline-block group">
                          <div className="h-20 w-20 rounded-xl border-2 border-purple-400 overflow-hidden bg-white flex items-center justify-center shadow-sm">
                            <img src={img.url} alt="Gallery item" className="max-h-full max-w-full object-contain" />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeImageFromArray(idx)}
                            className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-1 hover:bg-rose-600 transition-colors shadow"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => localFileRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-purple-500 transition-colors flex flex-col items-center justify-center bg-slate-50/20"
                    >
                      <input
                        ref={localFileRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <Plus className="text-purple-600 mb-1" size={20} />
                      <span className="text-xs font-semibold text-gray-700">Add from Files</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => cameraFileRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-blue-500 transition-colors flex flex-col items-center justify-center bg-slate-50/20"
                    >
                      <Plus className="text-blue-600 mb-1" size={20} />
                      <span className="text-xs font-semibold text-gray-700">Snap Photo</span>
                      <input
                        ref={cameraFileRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleImageChange(e, true)}
                        className="hidden"
                      />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Choose either option - browse files or capture photo from camera
                  </p>
                </div>

                {/* Featured Checkbox */}
                <div className="flex items-center col-span-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Featured Product</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-xl hover:shadow-lg transition-all font-medium"
                >
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl hover:bg-gray-200 transition-all font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;